"use client"

import * as React from "react"

import { CheckIcon, ChevronRightIcon, CircleIcon } from "@/components/ui/icons"
import {
  ClientPortal,
  Slot,
  type Align,
  type Side,
  mergeRefs,
  useAnchoredPosition,
  useControllableState,
  useDismissableLayer,
  useEscapeKey,
} from "@/components/ui/internal"
import { dropdownMenuClassNames } from "@/components/ui/uiClasses"
import { cn } from "@/lib/utils"

type DropdownMenuContextValue = {
  open: boolean
  setOpen: (open: boolean) => void
  triggerRef: React.RefObject<HTMLElement | null>
}

const DropdownMenuContext =
  React.createContext<DropdownMenuContextValue | null>(null)

function useDropdownMenuContext(component: string) {
  const context = React.useContext(DropdownMenuContext)
  if (!context) {
    throw new Error(`${component} must be used within DropdownMenu`)
  }
  return context
}

function DropdownMenu({
  open,
  defaultOpen = false,
  onOpenChange,
  children,
}: {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useControllableState({
    prop: open,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  })
  const triggerRef = React.useRef<HTMLElement | null>(null)

  return (
    <DropdownMenuContext.Provider
      value={{
        open: Boolean(isOpen),
        setOpen: (nextOpen) => setIsOpen(nextOpen),
        triggerRef,
      }}
    >
      {children}
    </DropdownMenuContext.Provider>
  )
}

function DropdownMenuPortal({ children }: { children: React.ReactNode }) {
  return <ClientPortal>{children}</ClientPortal>
}

const DropdownMenuTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement> & {
    asChild?: boolean
    children?: React.ReactNode
    type?: "button" | "submit" | "reset"
  }
>(function DropdownMenuTrigger(
  { asChild = false, onClick, children, ...props },
  forwardedRef
) {
  const dropdownMenu = useDropdownMenuContext("DropdownMenuTrigger")

  if (asChild) {
    return (
      <Slot
        ref={mergeRefs(forwardedRef, dropdownMenu.triggerRef)}
        data-slot="dropdown-menu-trigger"
        aria-expanded={dropdownMenu.open}
        aria-haspopup="menu"
        onClick={(event) => {
          onClick?.(event)
          dropdownMenu.setOpen(!dropdownMenu.open)
        }}
        {...props}
      >
        {children as React.ReactElement}
      </Slot>
    )
  }

  return (
    <button
      ref={mergeRefs(
        forwardedRef as React.Ref<HTMLButtonElement>,
        dropdownMenu.triggerRef as React.Ref<HTMLButtonElement>
      )}
      data-slot="dropdown-menu-trigger"
      aria-expanded={dropdownMenu.open}
      aria-haspopup="menu"
      type={props.type ?? "button"}
      onClick={(event) => {
        onClick?.(event)
        dropdownMenu.setOpen(!dropdownMenu.open)
      }}
      {...props}
    >
      {children}
    </button>
  )
})

const DropdownMenuContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    align?: Align
    side?: Side
    sideOffset?: number
  }
>(function DropdownMenuContent(
  {
    className,
    align = "center",
    side = "bottom",
    sideOffset = 4,
    onClick,
    ...props
  },
  forwardedRef
) {
  const dropdownMenu = useDropdownMenuContext("DropdownMenuContent")
  const contentRef = React.useRef<HTMLDivElement | null>(null)
  const { side: resolvedSide, style } = useAnchoredPosition({
    open: dropdownMenu.open,
    anchorRef: dropdownMenu.triggerRef,
    contentRef,
    side,
    align,
    sideOffset,
  })

  useDismissableLayer({
    enabled: dropdownMenu.open,
    refs: [dropdownMenu.triggerRef, contentRef],
    onDismiss: () => dropdownMenu.setOpen(false),
  })
  useEscapeKey(dropdownMenu.open, () => dropdownMenu.setOpen(false))

  if (!dropdownMenu.open) {
    return null
  }

  return (
    <DropdownMenuPortal>
      <div
        ref={mergeRefs(forwardedRef, contentRef)}
        data-slot="dropdown-menu-content"
        data-side={resolvedSide}
        role="menu"
        className={cn(dropdownMenuClassNames.content, className)}
        style={style}
        onClick={(event) => {
          event.stopPropagation()
          onClick?.(event)
        }}
        {...props}
      />
    </DropdownMenuPortal>
  )
})

function DropdownMenuGroup({ ...props }: React.ComponentProps<"div">) {
  return <div data-slot="dropdown-menu-group" role="group" {...props} />
}

type DropdownMenuItemProps = React.ComponentProps<"button"> & {
  inset?: boolean
  variant?: "default" | "destructive"
  onSelect?: (event: Event) => void
}

const DropdownMenuItem = React.forwardRef<
  HTMLButtonElement,
  DropdownMenuItemProps
>(function DropdownMenuItem(
  {
    className,
    inset,
    variant = "default",
    onClick,
    onSelect,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    disabled,
    ...props
  },
  ref
) {
  const dropdownMenu = useDropdownMenuContext("DropdownMenuItem")
  const [highlighted, setHighlighted] = React.useState(false)

  return (
    <button
      ref={ref}
      type="button"
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      data-highlighted={highlighted ? "" : undefined}
      role="menuitem"
      className={cn(dropdownMenuClassNames.item, className)}
      disabled={disabled}
      onMouseEnter={(event) => {
        onMouseEnter?.(event)
        setHighlighted(true)
      }}
      onMouseLeave={(event) => {
        onMouseLeave?.(event)
        setHighlighted(false)
      }}
      onFocus={(event) => {
        onFocus?.(event)
        setHighlighted(true)
      }}
      onBlur={(event) => {
        onBlur?.(event)
        setHighlighted(false)
      }}
      onClick={(event) => {
        onClick?.(event)
        if (disabled || event.defaultPrevented) {
          return
        }

        const selectEvent = new Event("select", { cancelable: true })
        onSelect?.(selectEvent)

        if (!selectEvent.defaultPrevented) {
          dropdownMenu.setOpen(false)
        }
      }}
      {...props}
    />
  )
})

const DropdownMenuCheckboxItem = React.forwardRef<
  HTMLButtonElement,
  DropdownMenuItemProps & {
    checked?: boolean
  }
>(function DropdownMenuCheckboxItem(
  { className, children, checked, ...props },
  ref
) {
  return (
    <DropdownMenuItem
      ref={ref}
      data-slot="dropdown-menu-checkbox-item"
      className={cn(dropdownMenuClassNames.itemWithIndicator, className)}
      {...props}
    >
      <span className={dropdownMenuClassNames.indicator}>
        {checked ? <CheckIcon /> : null}
      </span>
      {children}
    </DropdownMenuItem>
  )
})

function DropdownMenuRadioGroup({ ...props }: React.ComponentProps<"div">) {
  return <div data-slot="dropdown-menu-radio-group" role="group" {...props} />
}

const DropdownMenuRadioItem = React.forwardRef<
  HTMLButtonElement,
  DropdownMenuItemProps & {
    checked?: boolean
  }
>(function DropdownMenuRadioItem(
  { className, children, checked, ...props },
  ref
) {
  return (
    <DropdownMenuItem
      ref={ref}
      data-slot="dropdown-menu-radio-item"
      className={cn(dropdownMenuClassNames.itemWithIndicator, className)}
      {...props}
    >
      <span className={dropdownMenuClassNames.indicator}>
        {checked ? <CircleIcon /> : null}
      </span>
      {children}
    </DropdownMenuItem>
  )
})

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<"div"> & {
  inset?: boolean
}) {
  return (
    <div
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn(dropdownMenuClassNames.label, className)}
      {...props}
    />
  )
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dropdown-menu-separator"
      role="separator"
      className={cn(dropdownMenuClassNames.separator, className)}
      {...props}
    />
  )
}

function DropdownMenuShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(dropdownMenuClassNames.shortcut, className)}
      {...props}
    />
  )
}

function DropdownMenuSub({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

const DropdownMenuSubTrigger = React.forwardRef<
  HTMLButtonElement,
  DropdownMenuItemProps
>(function DropdownMenuSubTrigger({ children, ...props }, ref) {
  return (
    <DropdownMenuItem ref={ref} data-slot="dropdown-menu-sub-trigger" {...props}>
      {children}
      <ChevronRightIcon className={dropdownMenuClassNames.subChevron} />
    </DropdownMenuItem>
  )
})

const DropdownMenuSubContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(function DropdownMenuSubContent({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      data-slot="dropdown-menu-sub-content"
      className={cn(dropdownMenuClassNames.subContent, className)}
      {...props}
    />
  )
})

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
}
